import { useCallback, useEffect, useState } from "react";
import { getMonthlyPerformance } from "../../../services/clearing/station-performance";
import type { MonthlyReport, ReportingOptions } from "../../../types/stationPerformance";

interface UseMonthlyPerformanceArgs {
  month: string;
  options: ReportingOptions;
  sort?: string;
  order?: "asc" | "desc";
  page?: number;
  limit?: number;
}

/**
 * The monthly scorecard.
 *
 * `neverComputed` is kept separate from `error`: a month that has not been aggregated is an expected
 * state with a specific remedy (run the backfill), not a failure. Collapsing the two would tell the
 * reader something went wrong when nothing did.
 */
export const useMonthlyPerformance = ({
  month,
  options,
  sort = "moneyCents",
  order = "desc",
  page = 1,
  limit = 50,
}: UseMonthlyPerformanceArgs) => {
  const [report, setReport] = useState<MonthlyReport | null>(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [neverComputed, setNeverComputed] = useState<string[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  // When the shown report was read: the month-end projection counts today up to this moment (BOF-84),
  // so a tab left open keeps figures and projection in step until the next fetch.
  const [fetchedAt, setFetchedAt] = useState<Date | null>(null);

  const fetchReport = useCallback(async () => {
    if (!month) return;
    // Distinguish first paint from a background refresh, so switching a lens does not blank the table.
    if (report === null) setInitialLoading(true);
    else setRefreshing(true);
    setNeverComputed(null);
    setError(null);

    const requestedAt = new Date();
    const result = await getMonthlyPerformance({ month, options, sort, order, page, limit });
    if (result?.success) {
      setReport(result.data ?? null);
      setFetchedAt(requestedAt);
    } else if (result?.neverComputed) {
      setNeverComputed(result.months ?? [month]);
      setReport(null);
    } else {
      setError(result?.message ?? "Could not load station performance.");
    }
    setInitialLoading(false);
    setRefreshing(false);
    // `report` is deliberately excluded: including it would re-fetch on every successful fetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [month, options, sort, order, page, limit]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  return { report, fetchedAt, initialLoading, refreshing, neverComputed, error, fetchReport };
};
